import { FC, useEffect, useMemo } from 'react';
import { Grid, Typography, Divider, Box, Chip } from '@mui/material';
import { useForm } from 'react-hook-form';
import { z, ZodType } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import {
  TStaffDetailsResponse,
  EMPLOYMENT_STATUS,
  TPreviousExperience,
} from '../../../../../apis/staff/staff.interfaces';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledTypeahead from '../../../../components/controlledComponents/ControlledTypeahead.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import ControlledDateField from 'src/views/components/controlledComponents/ControlledDateField.component';
import { useStaffEligibleUsersHook } from '../../hooks/useStaffEligibleUsersHook';
import { useSupervisorsHook } from '../../hooks/useSupervisorsHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { TStaffEligibleUser } from 'src/apis/users/users.interfaces';
import { localStorageGet } from 'src/utils/localStorage';

interface StaffManageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  errorMessage: string;
  staffDetails: Partial<TStaffDetailsResponse> | null;
  isLoading: boolean;
  onSubmitForm?: (data: StaffFormData, isUpdate: boolean) => void;
}

export type StaffFormData = {
  userId: string;
  supervisorId?: string;
  designation: string;
  department: string;
  joiningDate: string;
  employmentStatus: EMPLOYMENT_STATUS | 'select';
};

const getCreateStaffFormSchema = (
  translateText: TTranslateTextType
): ZodType<StaffFormData> =>
  z.object({
    userId: z.string().min(1, translateText('staff.manage.validation.userRequired')),
    supervisorId: z.string().optional().default(''),
    designation: z
      .string()
      .min(1, translateText('staff.manage.validation.designationRequired')),
    department: z
      .string()
      .min(1, translateText('staff.manage.validation.departmentRequired')),
    joiningDate: z
      .string()
      .min(1, translateText('staff.manage.validation.joiningDateRequired')),
    employmentStatus: z.nativeEnum(EMPLOYMENT_STATUS),
  });

type EditStaffFormData = {
  designation: string;
  department: string;
  joiningDate: string;
  employmentStatus: EMPLOYMENT_STATUS | 'select';
};

const getEditStaffFormSchema = (
  translateText: TTranslateTextType
): ZodType<EditStaffFormData> =>
  z.object({
    designation: z
      .string()
      .min(1, translateText('staff.manage.validation.designationRequired')),
    department: z
      .string()
      .min(1, translateText('staff.manage.validation.departmentRequired')),
    joiningDate: z
      .string()
      .min(1, translateText('staff.manage.validation.joiningDateRequired')),
    employmentStatus: z.nativeEnum(EMPLOYMENT_STATUS),
  });

const createDefaultValues: StaffFormData = {
  userId: '',
  supervisorId: '',
  designation: '',
  department: '',
  joiningDate: '',
  employmentStatus: 'select',
};

const editDefaultValues: EditStaffFormData = {
  designation: '',
  department: '',
  joiningDate: '',
  employmentStatus: 'select',
};

/**
 * Helper to get user context from JWT/localStorage
 */
const getUserContext = () => {
  const loggedInUser = localStorageGet('loggedInUser', undefined) as
    | { id?: string; instituteId?: string }
    | undefined;

  // Decode JWT for institute_ref
  const token = localStorageGet('token', '') as string;
  let instituteId = loggedInUser?.instituteId || '';
  let userId = loggedInUser?.id || '';

  if (token) {
    try {
      const encodedPayload = token.split('.')[1];
      if (encodedPayload) {
        const payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = payload.padEnd(
          payload.length + ((4 - (payload.length % 4)) % 4),
          '='
        );
        const decoded = JSON.parse(window.atob(paddedPayload));
        instituteId = instituteId || decoded.institute_ref || '';
        userId = userId || decoded._id || '';
      }
    } catch {
      // ignore decode errors
    }
  }

  return { instituteId, userId };
};

const StaffManageDialog: FC<StaffManageDialogProps> = ({
  isOpen,
  onClose,
  errorMessage,
  staffDetails,
  isLoading,
  onSubmitForm,
}) => {
  const { translateText } = useTranslation();
  const isEditMode = !!staffDetails;
  const { instituteId } = getUserContext();

  // Fetch eligible users and supervisors for create mode
  const { data: eligibleUsersResponse } = useStaffEligibleUsersHook(
    instituteId,
    undefined
  );
  const { data: supervisorsResponse } = useSupervisorsHook(instituteId);

  // Build options for typeaheads
  const userOptions = useMemo(() => {
    if (!eligibleUsersResponse?.data) return [] as any[];
    return eligibleUsersResponse.data.map((user: TStaffEligibleUser) => ({
      key: user.id,
      value:
        `${user.firstName} ${user.middleName || ''} ${user.lastName}`.trim() +
        (user.hasStaffRecord ? ` ${translateText('staff.manage.alreadyRegistered')}` : ''),
      disabled: user.hasStaffRecord,
    })) as any[];
  }, [eligibleUsersResponse, translateText]);

  const supervisorOptions = useMemo<TKeyValue<string>[]>(() => {
    if (!supervisorsResponse?.data) return [];
    return supervisorsResponse.data.map((user) => ({
      key: user.id,
      value: `${user.firstName} ${user.middleName || ''} ${user.lastName}`.trim(),
    }));
  }, [supervisorsResponse]);

  // Get selected user's details (addressId, contactId) for submission
  const getSelectedUserDetails = (selectedUserId: string) => {
    if (!eligibleUsersResponse?.data) return null;
    return eligibleUsersResponse.data.find((u) => u.id === selectedUserId) || null;
  };

  // Create mode form
  const createForm = useForm<StaffFormData>({
    resolver: zodResolver(getCreateStaffFormSchema(translateText)),
    mode: 'onBlur',
    defaultValues: createDefaultValues,
  });

  // Edit mode form
  const editForm = useForm<EditStaffFormData>({
    resolver: zodResolver(getEditStaffFormSchema(translateText)),
    mode: 'onBlur',
    defaultValues: editDefaultValues,
  });

  useEffect(() => {
    if (isEditMode && staffDetails) {
      editForm.reset({
        designation: staffDetails.designation || '',
        department: staffDetails.department || '',
        joiningDate: staffDetails.joiningDate
          ? new Date(staffDetails.joiningDate).toISOString().split('T')[0]
          : '',
        employmentStatus: staffDetails.employmentStatus ?? 'select',
      });
    } else {
      createForm.reset(createDefaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffDetails, isEditMode]);

  const onCreateSubmit = (data: StaffFormData) => {
    if (onSubmitForm) {
      onSubmitForm(data, false);
    }
    onClose();
  };

  const onEditSubmit = (data: EditStaffFormData) => {
    if (onSubmitForm) {
      // Pass edit data with empty userId/supervisorId (not relevant for update)
      onSubmitForm(
        { ...data, userId: '', supervisorId: '' } as StaffFormData,
        true
      );
    }
    onClose();
  };

  const handleSubmitAction = isEditMode
    ? editForm.handleSubmit(onEditSubmit)
    : createForm.handleSubmit(onCreateSubmit);

  return (
    <StructuredModal
      isOpen={isOpen}
      onClose={onClose}
      drawerWidth="70vw"
      title={
        isEditMode
          ? translateText('staff.manage.updateStaff')
          : translateText('staff.manage.createStaff')
      }
      footerPositiveAction={handleSubmitAction}
      footerPositiveActionText={
        isEditMode
          ? translateText('staff.manage.update')
          : translateText('staff.manage.create')
      }
      footerNegativeAction={onClose}
      footerNegativeActionText={translateText('staff.manage.cancel')}
      footerMessage={
        errorMessage ? (
          <Typography
            color="error"
            variant="body2"
            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
          >
            {errorMessage}
          </Typography>
        ) : null
      }
    >
      <form
        onSubmit={handleSubmitAction}
        style={{ width: '100%' }}
      >
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ p: { xs: 1, sm: 2 } }}>
          {/* ===== CREATE MODE ===== */}
          {!isEditMode && (
            <>
              {/* User Selection Section */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                  gutterBottom
                >
                  {translateText('staff.manage.selectUser')}
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTypeahead
                  id="userId"
                  label={translateText('staff.manage.fields.user')}
                  name="userId"
                  control={createForm.control}
                  options={userOptions.filter((opt) => !opt.disabled)}
                  errorMessage={createForm.formState.errors.userId?.message}
                  isRequired
                  isGridItem={false}
                  getOptionDisabled={(option: any) => option.disabled}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTypeahead
                  id="supervisorId"
                  label={translateText('staff.manage.fields.supervisor')}
                  name="supervisorId"
                  control={createForm.control}
                  options={supervisorOptions}
                  errorMessage={createForm.formState.errors.supervisorId?.message}
                  isGridItem={false}
                />
              </Grid>

              {/* Selected user info chip */}
              {createForm.watch('userId') && (
                <Grid item xs={12}>
                  {(() => {
                    const selectedUser = getSelectedUserDetails(createForm.watch('userId'));
                    if (!selectedUser) return null;
                    return (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={translateText('staff.manage.selectedUserRole', {
                            role: selectedUser.userRole,
                          })}
                          size="small"
                          variant="outlined"
                        />
                        {selectedUser.email && (
                          <Chip
                            label={translateText('staff.manage.selectedUserEmail', {
                              email: selectedUser.email,
                            })}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    );
                  })()}
                </Grid>
              )}

              {/* Staff Information Section */}
              <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                  gutterBottom
                >
                  {translateText('staff.manage.staffInformation')}
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  id="designation"
                  label={translateText('staff.manage.fields.designation')}
                  name="designation"
                  control={createForm.control}
                  errorMessage={createForm.formState.errors.designation?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  id="department"
                  label={translateText('staff.manage.fields.department')}
                  name="department"
                  control={createForm.control}
                  errorMessage={createForm.formState.errors.department?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledDateField
                  id="joiningDate"
                  label={translateText('staff.manage.fields.joiningDate')}
                  name="joiningDate"
                  control={createForm.control}
                  errorMessage={createForm.formState.errors.joiningDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledSelectField
                  label={translateText('staff.manage.fields.employmentStatus')}
                  id="employmentStatus"
                  name="employmentStatus"
                  control={createForm.control}
                  isRequired
                  itemList={[
                    { key: EMPLOYMENT_STATUS.ACTIVE, value: translateText('staff.manage.employmentStatus.active') },
                    { key: EMPLOYMENT_STATUS.INACTIVE, value: translateText('staff.manage.employmentStatus.inactive') },
                    { key: EMPLOYMENT_STATUS.ON_LEAVE, value: translateText('staff.manage.employmentStatus.onLeave') },
                    { key: EMPLOYMENT_STATUS.RETIRED, value: translateText('staff.manage.employmentStatus.retired') },
                  ]}
                  errorMessage={createForm.formState.errors.employmentStatus?.message}
                />
              </Grid>
            </>
          )}

          {/* ===== EDIT MODE ===== */}
          {isEditMode && (
            <>
              {/* Read-only user info header */}
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                  gutterBottom
                >
                  {translateText('staff.manage.staffMember')}
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip
                    label={`${staffDetails?.basicDetails?.firstName || ''} ${staffDetails?.basicDetails?.middleName || ''} ${staffDetails?.basicDetails?.lastName || ''}`.trim()}
                    color="primary"
                    variant="outlined"
                  />
                  {staffDetails?.contactDetails?.email?.email && (
                    <Chip label={staffDetails.contactDetails.email.email} size="small" variant="outlined" />
                  )}
                </Box>
              </Grid>

              {/* Staff Information Section */}
              <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                  gutterBottom
                >
                  {translateText('staff.manage.staffInformation')}
                </Typography>
                <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  id="designation"
                  label={translateText('staff.manage.fields.designation')}
                  name="designation"
                  control={editForm.control}
                  errorMessage={editForm.formState.errors.designation?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  id="department"
                  label={translateText('staff.manage.fields.department')}
                  name="department"
                  control={editForm.control}
                  errorMessage={editForm.formState.errors.department?.message}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledDateField
                  id="joiningDate"
                  label={translateText('staff.manage.fields.joiningDate')}
                  name="joiningDate"
                  control={editForm.control}
                  errorMessage={editForm.formState.errors.joiningDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ControlledSelectField
                  label={translateText('staff.manage.fields.employmentStatus')}
                  id="employmentStatus"
                  name="employmentStatus"
                  control={editForm.control}
                  isRequired
                  itemList={[
                    { key: EMPLOYMENT_STATUS.ACTIVE, value: translateText('staff.manage.employmentStatus.active') },
                    { key: EMPLOYMENT_STATUS.INACTIVE, value: translateText('staff.manage.employmentStatus.inactive') },
                    { key: EMPLOYMENT_STATUS.ON_LEAVE, value: translateText('staff.manage.employmentStatus.onLeave') },
                    { key: EMPLOYMENT_STATUS.RETIRED, value: translateText('staff.manage.employmentStatus.retired') },
                  ]}
                  errorMessage={editForm.formState.errors.employmentStatus?.message}
                />
              </Grid>

              {/* Previous Experience Section (read-only) */}
              {staffDetails?.previousExperience &&
                staffDetails.previousExperience.length > 0 && (
                  <>
                    <Grid item xs={12} sx={{ mt: { xs: 1, sm: 2 } }}>
                      <Typography
                        variant="h6"
                        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                        gutterBottom
                      >
                        {translateText('staff.profile.previousExperience.title')}
                      </Typography>
                      <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
                    </Grid>
                    {staffDetails.previousExperience.map(
                      (exp: TPreviousExperience, index: number) => (
                        <Grid item xs={12} key={`prev-exp-${index}`}>
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'divider',
                            }}
                          >
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary">
                                  {translateText('staff.profile.previousExperience.company')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {exp.company}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary">
                                  {translateText('staff.profile.previousExperience.role')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {exp.role}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary">
                                  {translateText('staff.profile.previousExperience.from')}
                                </Typography>
                                <Typography variant="body2">
                                  {exp.from ? new Date(exp.from).toLocaleDateString() : '-'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary">
                                  {translateText('staff.profile.previousExperience.to')}
                                </Typography>
                                <Typography variant="body2">
                                  {exp.to ? new Date(exp.to).toLocaleDateString() : '-'}
                                </Typography>
                              </Grid>
                              {exp.description && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="textSecondary">
                                    {translateText('staff.profile.previousExperience.description')}
                                  </Typography>
                                  <Typography variant="body2">{exp.description}</Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )
                    )}
                  </>
                )}
            </>
          )}
        </Grid>
      </form>
    </StructuredModal>
  );
};

export { getUserContext };
export default StaffManageDialog;
